import {TAppDispatch, TRootState} from "types";
import mapXCandidateApi from "api/candidateApi";
import {SICandidate, TGenerateSummariesApiPayload} from "api/candidateApi/types";
import {
	targetListCandidatesSelector,
	targetListIDSelector,
} from "store/target-list/targetListSelectors";
import {successResponse} from "helpers/map";
import {getAllGenerateSummaryCandidateIdsForCurrentProjectSelector} from "store/project/GenerateSummary/generateCandidateSummarySelectors";
import {
	setAPCandidatesWithUpdatedSummary,
	setGenerateSummaryCandidateIdsForCurrentProject,
	updateProjectSavedPeopleWithSummary,
} from "store/project/projectActions";

import {ICandidateByIdsApiRequestPayload} from "api/candidateApi/form";
import axios from "axios";
import {updateTargetListCandidatesWithSummary} from "store/target-list/targetListActions";
import {updateCandidateProfileAfterRefresh} from "store/refresh-candidate/refreshCandidateActions";
import {userEmailSelector} from "store/user/userSelectors";
import {Mixpanel} from "helpers/mixpanel";
import {STProject} from "api/projectApi/types";
import {projectSelector} from "store/project-list/projectListSelectors";
import {TUpdateProjectCandidateInfoForm} from "api/projectApi/projectCandidatesApi/types";
import projectCandidateApi from "api/projectApi/projectCandidatesApi";
import {projectSavedPeopleSelector} from "store/project/projectSelectors";
import {getPaginatedProjectSavedPeople} from "store/project/projectCandidatesAsyncActions";

/* eslint-disable  @typescript-eslint/no-explicit-any */
const cancelTokens = {} as any;

/**
 * Asks the API to generate summaries for `candidateIds` (plus any already in flight for this
 * target list, so the poller keeps tracking them). Resolves to whether the server accepted.
 */
export const generateCandidatesSummary =
	(candidateIds: number[]) =>
	async (dispatch: TAppDispatch, getState: () => TRootState): Promise<boolean> => {
		try {
			const state = getState();

			const targetListId = targetListIDSelector(state);

			const allIds = getAllGenerateSummaryCandidateIdsForCurrentProjectSelector(
				state,
				targetListId,
			);

			let ids: number[] = [];

			if (allIds && Array.isArray(allIds)) {
				ids = allIds;
			}

			ids = [...candidateIds, ...ids];

			const apiPayload: TGenerateSummariesApiPayload = {
				candidate_ids: ids,
				target_list_id: targetListId,
			};

			const response = await mapXCandidateApi.generateSummaries(apiPayload);

			if (!successResponse(response, 200)) return false;

			// Only mark candidates as in progress once the server has accepted the request,
			// otherwise a failed request would leave them "generating" until the poller gives up.
			dispatch(setGenerateSummaryCandidateIdsForCurrentProject({[targetListId]: ids}));

			const userEmail = userEmailSelector(state);

			Mixpanel.track(`Candidate Summary Generated`, {
				name: `${window.name}`,
				pageTitle: `${window.name}`,
				url: window.location.href,
				distinct_id: userEmail,
			});

			return true;
		} catch (e) {
			console.error(e);

			return false;
		}
	};

export const checkCandidateSummaryGeneratedStatus =
	(candidateIds: number[]) => async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const requestKey = `GET_GENERATE_SUMMARY_CANDIDATE_STATUS`;

		if (requestKey in cancelTokens) {
			cancelTokens[requestKey].cancel("Operation canceled due to new request.");
		}

		cancelTokens[requestKey] = axios.CancelToken.source();

		const config = {
			cancelToken: cancelTokens[requestKey].token,
		};

		const state = getState();

		const targetListId = targetListIDSelector(state);

		try {
			const apiPayload: ICandidateByIdsApiRequestPayload = {
				filters: {
					candidates: candidateIds,
					target_list: targetListId,
				},
			};

			const response = await mapXCandidateApi.getCandidatesByIds(apiPayload, config);
			if (successResponse(response, 200)) {
				const results = response.data.results;

				const savedPeoplesInState = targetListCandidatesSelector(state);
				const projectSavedPeopleForState = projectSavedPeopleSelector(state);

				let updatedIds = [...candidateIds];

				const updatedPeopleForState: SICandidate[] = [];
				const updatedSavedPeopleForState: SICandidate[] = [];

				savedPeoplesInState.forEach((candidate: SICandidate) => {
					const isCandidateInList = candidateIds.includes(candidate.id);
					const candidateFromResults = results.find(
						(c: SICandidate) => c.id === candidate.id,
					);
					const updatedCandidate = candidateFromResults ?? candidate;

					if (isCandidateInList && updatedCandidate.summary !== null) {
						dispatch(updateCandidateProfileAfterRefresh(updatedCandidate));
						updatedIds = updatedIds.filter((id) => id !== candidate.id);
					}

					updatedPeopleForState.push(updatedCandidate);

					if (
						projectSavedPeopleForState.some((c: SICandidate) => c.id === candidate.id)
					) {
						updatedSavedPeopleForState.push(updatedCandidate);
					}
				});

				dispatch(updateTargetListCandidatesWithSummary(updatedPeopleForState));

				dispatch(updateProjectSavedPeopleWithSummary(updatedSavedPeopleForState));

				dispatch(
					setGenerateSummaryCandidateIdsForCurrentProject({
						[targetListId]: [...updatedIds],
					}),
				);

				if (updatedIds.length === 0) {
					dispatch(getPaginatedProjectSavedPeople());
				}
			}
		} catch (e) {
			console.error(e);
		}
	};

export const updateCandidateSummary =
	(candidateId: number, summaryText: string) =>
	async (dispatch: TAppDispatch, getState: () => TRootState) => {
		const state = getState();

		const project = projectSelector(state) as STProject;

		let response;

		try {
			const formData: TUpdateProjectCandidateInfoForm = {
				summary: summaryText,
			};

			response = await projectCandidateApi.updateCandidateInfoByCandidateId(
				project.id,
				candidateId,
				formData,
			);

			if (successResponse(response, 200)) {
				const payload = {
					id: candidateId,
					summary: summaryText,
				};

				dispatch(setAPCandidatesWithUpdatedSummary(payload));

				return "success";
			} else {
				return "error";
			}
		} catch (e) {
			console.log(`Error from updating candidate summary: ${e}`);

			return "error";
		}
	};
